<template>
  <main-layout>
    <template #header>
      <ops-view-tabs />
    </template>

    <template #toolbar>
      <div class="filters">
        <span class="period-label">기간</span>
        <div class="period-presets">
          <button
            v-for="preset in presets"
            :key="preset.key"
            type="button"
            class="period-chip"
            :class="{ active: periodKey === preset.key }"
            @click="applyPreset(preset.key)"
          >
            {{ preset.label }}
          </button>
        </div>
        <select v-model="dateType" class="period-select" @change="onDateTypeChange">
          <option value="M">매출월</option>
          <option value="R">등록일</option>
        </select>
        <input v-model="startInput" :type="dateType === 'M' ? 'month' : 'date'" class="period-date" @change="onManualRange">
        <span class="period-tilde">~</span>
        <input v-model="endInput" :type="dateType === 'M' ? 'month' : 'date'" class="period-date" @change="onManualRange">
        <v-select
          v-model="actualYn"
          :items="actualOptions"
          item-text="text"
          item-value="value"
          dense
          hide-details
          label="실사용"
          style="max-width: 140px;"
          class="mr-3"
          @change="reload"
        />
        <v-select
          v-model="groupId"
          :items="groups"
          item-text="groupName"
          item-value="rentalcompanyGroupId"
          dense
          hide-details
          clearable
          label="그룹"
          style="max-width: 200px;"
          @change="reload"
        />
        <v-chip v-if="rentalcompanyId" class="ml-3" close small @click:close="clearCompany">
          {{ selectedName || rentalcompanyId }}
        </v-chip>
      </div>
    </template>

    <template #body>
      <div class="dashboard-scroll">
      <div v-if="errorMessage" class="error-box">{{ errorMessage }}</div>
      <div v-if="loading" class="loading-box">
        <v-progress-circular indeterminate color="primary" />
      </div>
      <div v-else class="dashboard">
        <section class="kpi-grid">
          <article v-for="card in kpiCards" :key="card.label" class="kpi-card">
            <div class="kpi-label">{{ card.label }}</div>
            <div class="kpi-value">{{ card.value }}</div>
            <div v-if="card.sub" class="kpi-sub">{{ card.sub }}</div>
          </article>
        </section>

        <section class="trend-grid">
          <article class="panel">
            <header>
              <h3>월별 모니터링</h3>
              <span>{{ trendCaption(finalTrend, false) }}</span>
            </header>
            <p class="hint">지난달은 청구에 저장된 모니터링 수입니다. 이번 달은 1일 이후 변경일시가 있는 프린터 수입니다.</p>
            <p v-if="finalTrend.length === 0" class="empty">월별 모니터링 집계를 가져오지 못했습니다.</p>
            <svg v-else class="line-chart" viewBox="0 0 760 240">
              <line v-for="tick in chartAxis(finalTrend, finalScale())" :key="'cg-' + tick.value" :x1="86" :x2="704" :y1="tick.y" :y2="tick.y" class="grid" />
              <text v-for="tick in chartAxis(finalTrend, finalScale())" :key="'ct-' + tick.value" x="80" :y="tick.y + 4" text-anchor="end" class="axis-label">{{ tick.text }}</text>
              <polyline :points="poly(chartPoints(finalTrend, false, finalScale()))" class="line line-final" />
              <g v-for="point in chartPoints(finalTrend, false, finalScale())" :key="'f-' + point.month">
                <circle :cx="point.x" :cy="point.y" r="4.5" class="dot dot-final" />
                <text :x="point.x" :y="point.labelY" text-anchor="middle" class="y-label">{{ point.predicted ? point.text + ' 예측' : point.text }}</text>
                <text :x="point.x" y="228" text-anchor="middle" class="x-label">{{ point.month }}</text>
                <title>{{ point.month }} {{ point.text }}</title>
              </g>
            </svg>
          </article>
          <article class="panel">
            <header>
              <h3>임대 계약</h3>
              <span>{{ trendCaption(contractTrend, false) }}</span>
            </header>
            <p class="hint">지난달은 청구에 저장된 계약 수입니다. 이번 달은 해지되지 않은 계약중 수입니다.</p>
            <p v-if="contractTrend.length === 0" class="empty">월별 계약 집계를 가져오지 못했습니다.</p>
            <svg v-else class="line-chart" viewBox="0 0 760 240">
              <line v-for="tick in chartAxis(contractTrend)" :key="'kg-' + tick.value" :x1="86" :x2="704" :y1="tick.y" :y2="tick.y" class="grid" />
              <text v-for="tick in chartAxis(contractTrend)" :key="'kt-' + tick.value" x="80" :y="tick.y + 4" text-anchor="end" class="axis-label">{{ tick.text }}</text>
              <polyline :points="poly(chartPoints(contractTrend, false))" class="line line-contract" />
              <g v-for="point in chartPoints(contractTrend, false)" :key="'k-' + point.month">
                <circle :cx="point.x" :cy="point.y" r="4.5" class="dot dot-contract" />
                <text :x="point.x" :y="point.labelY" text-anchor="middle" class="y-label">{{ point.text }}</text>
                <text :x="point.x" y="228" text-anchor="middle" class="x-label">{{ point.month }}</text>
                <title>{{ point.month }} {{ point.text }}</title>
              </g>
            </svg>
          </article>
          <article class="panel">
            <header>
              <h3>매출</h3>
              <span>{{ trendCaption(salesTrend, true) }}</span>
            </header>
            <p class="hint">최근 6개월 매출월 청구액입니다. 가로는 매출월, 세로는 청구 합계이며 ERP 매출월과 같은 기준입니다.</p>
            <p v-if="salesTrend.length === 0" class="empty">월별 매출 집계를 가져오지 못했습니다.</p>
            <svg v-else class="line-chart" viewBox="0 0 760 240">
              <line v-for="tick in chartAxis(salesTrend)" :key="'sg-' + tick.value" :x1="86" :x2="704" :y1="tick.y" :y2="tick.y" class="grid" />
              <text v-for="tick in chartAxis(salesTrend)" :key="'st-' + tick.value" x="80" :y="tick.y + 4" text-anchor="end" class="axis-label">{{ tick.text }}</text>
              <polyline :points="poly(chartPoints(salesTrend, true))" class="line line-sales" />
              <g v-for="point in chartPoints(salesTrend, true)" :key="'s-' + point.month">
                <circle :cx="point.x" :cy="point.y" r="4.5" class="dot dot-sales" />
                <text :x="point.x" :y="point.labelY" text-anchor="middle" class="y-label">{{ point.text }}</text>
                <text :x="point.x" y="228" text-anchor="middle" class="x-label">{{ point.month }}</text>
                <title>{{ point.month }} {{ point.text }}</title>
              </g>
            </svg>
          </article>
          <article class="panel">
            <header>
              <h3>청구 금액</h3>
              <span>{{ trendCaption(billingTrend, true) }}</span>
            </header>
            <p class="hint">최근 6개월 청구월 기준입니다. 금액은 청구 합계이고, 계약서·잉크젯·복합기는 그 안의 항목 금액입니다.</p>
            <div class="legend">
              <span class="leg leg-bill">금액</span>
              <span class="leg leg-bill-contract">계약서</span>
              <span class="leg leg-bill-ink">잉크젯</span>
              <span class="leg leg-bill-multi">복합기</span>
            </div>
            <p v-if="billingTrend.length === 0" class="empty">월별 청구 금액 집계를 가져오지 못했습니다.</p>
            <svg v-else class="line-chart" viewBox="0 0 760 240">
              <line v-for="tick in chartAxis(billingTrend, billingScale())" :key="'bg-' + tick.value" :x1="86" :x2="704" :y1="tick.y" :y2="tick.y" class="grid" />
              <text v-for="tick in chartAxis(billingTrend, billingScale())" :key="'bt-' + tick.value" x="80" :y="tick.y + 4" text-anchor="end" class="axis-label">{{ tick.text }}</text>
              <polyline :points="poly(chartPoints(billingMultiTrend, true, billingScale()))" class="line line-bill-multi" />
              <polyline :points="poly(chartPoints(billingInkTrend, true, billingScale()))" class="line line-bill-ink" />
              <polyline :points="poly(chartPoints(billingContractTrend, true, billingScale()))" class="line line-bill-contract" />
              <polyline :points="poly(chartPoints(billingTrend, true, billingScale()))" class="line line-bill" />
              <g v-for="(point, index) in chartPoints(billingTrend, true, billingScale())" :key="'b-' + point.month">
                <circle :cx="point.x" :cy="point.y" r="4.5" class="dot dot-bill" />
                <text :x="point.x" :y="point.labelY" text-anchor="middle" class="y-label">{{ point.text }}</text>
                <text :x="point.x" y="228" text-anchor="middle" class="x-label">{{ point.month }}</text>
                <title>{{ point.month }} 금액 {{ point.text }} / 계약서 {{ moneyAt(billingContractTrend, index) }} / 잉크젯 {{ moneyAt(billingInkTrend, index) }} / 복합기 {{ moneyAt(billingMultiTrend, index) }}</title>
              </g>
            </svg>
          </article>
        </section>

        <section v-if="rentalcompanyId" class="panel">
          <header>
            <h3>운영 인사이트</h3>
            <span>직전 동일 기간과 비교한 규칙 {{ insights.length }}건</span>
          </header>
          <p v-if="insights.length === 0" class="empty">이 조건에서 걸린 인사이트가 없습니다.</p>
          <button
            v-for="item in insights"
            :key="item.code + '-' + (item.rentalcompanyId || 'all') + item.title"
            type="button"
            class="insight"
            :class="item.severity === 'DANGER' ? 'danger' : 'warn'"
            @click="openInsight(item)"
          >
            <strong>{{ item.title }}</strong>
            <span>{{ item.message }}</span>
          </button>
        </section>

      </div>
      </div>
    </template>
  </main-layout>
</template>

<script>
import MainLayout from "@/components/common/layout/MainLayout";
import OpsViewTabs from "@/views/dashboard/OpsViewTabs";
import {PATH} from "@/constants";
import {DateTime} from "luxon";

export default {
  name: "Dashboard",
  components: {OpsViewTabs, MainLayout},
  data() {
    return {
      loading: false,
      errorMessage: "",
      periodKey: "30d",
      dateType: "M",
      startInput: DateTime.local().toFormat("yyyy-MM"),
      endInput: DateTime.local().toFormat("yyyy-MM"),
      presets: [
        {key: "today", label: "오늘"},
        {key: "7d", label: "7일"},
        {key: "30d", label: "30일"},
        {key: "thisMonth", label: "이번달"},
        {key: "lastMonth", label: "지난달"},
      ],
      actualYn: "Y",
      actualOptions: [
        {text: "실사용", value: "Y"},
        {text: "전체", value: "ALL"},
      ],
      groupId: null,
      groups: [],
      rentalcompanyId: null,
      selectedName: "",
      routeStamp: "",
      pageNumber: 1,
      pageSize: 15,
      sortKey: "sales",
      descending: true,
      totalCount: 0,
      kpi: {},
      collectedTrend: [],
      addedTrend: [],
      silentTrend: [],
      finalTrend: [],
      contractTrend: [],
      salesTrend: [],
      salesMonths: [],
      billingTrend: [],
      billingContractTrend: [],
      billingInkTrend: [],
      billingMultiTrend: [],
      directions: [],
      insights: [],
      companies: [],
    }
  },
  computed: {
    pageLength() {
      if (!this.totalCount) return 1
      return Math.ceil(this.totalCount / this.pageSize)
    },
    collectedMax() {
      return this.maxOf(this.collectedTrend)
    },
    contractMax() {
      return this.maxOf(this.contractTrend)
    },
    salesMax() {
      return this.maxOf(this.salesTrend)
    },
    kpiCards() {
      const kpi = this.kpi || {}
      return [
        {label: "업체", value: this.count(kpi.companyCnt)},
        {label: "기간 수신 프린터", value: this.count(kpi.collectedCnt), sub: "등록 " + this.count(kpi.registeredCnt) + " · 미수집 " + this.count(kpi.silentCnt)},
        {label: "장치 PC", value: this.count(kpi.devicePcCnt) + " / " + this.count(kpi.devicePcHeard), sub: "DV " + this.count(kpi.deviceDvCnt) + " / " + this.count(kpi.deviceDvHeard) + " · 등록/기간접속"},
        {label: "활성 계약", value: this.count(kpi.contractActive), sub: "신규 " + this.count(kpi.contractNew)},
        {label: "매출", value: this.money(kpi.salesAmount), sub: "미수 " + this.money(kpi.salesBalance)},
        {label: "청구 금액", value: this.money(kpi.billingAmount)},
        {label: "기본요금", value: this.money(kpi.billingBaseAmount)},
        {label: "계약서 금액", value: this.money(kpi.billingContractAmount)},
        {label: "잉크젯 금액", value: this.money(kpi.billingInkAmount)},
        {label: "복합기 금액", value: this.money(kpi.billingMultiAmount)},
        {label: "매입", value: this.money(kpi.purchaseAmount)},
        {label: "사용자", value: this.count(kpi.userCnt)},
        {label: "로그인", value: this.count(kpi.loginCnt), sub: this.count(kpi.loginUserCnt) + "명"},
        {label: "CMS 성공", value: this.rate(kpi.cmsSuccess, kpi.cmsTotal), sub: this.count(kpi.cmsSuccess) + "/" + this.count(kpi.cmsTotal)},
        {label: "세금계산서", value: this.rate(kpi.taxSuccess, kpi.taxTotal), sub: this.count(kpi.taxSuccess) + "/" + this.count(kpi.taxTotal)},
        {label: "거래명세서", value: this.rate(kpi.stmtSuccess, kpi.stmtTotal)},
        {label: "AI 호출", value: this.count(kpi.aiCallCnt)},
      ].filter((card) => this.rentalcompanyId || ["로그인", "CMS 성공", "세금계산서", "거래명세서", "AI 호출"].indexOf(card.label) < 0)
    },
  },
  mounted() {
    this.loadGroups()
    this.applyRoute()
    this.reload()
  },
  activated() {
    const query = this.$route.query || {}
    const openedFromRank = query.rentalcompanyId || query.start || query.dateType || query.periodKey
    if (!openedFromRank) return
    if (this.$route.fullPath === this.routeStamp) return
    this.applyRoute()
    this.reload()
  },
  methods: {
    async loadGroups() {
      try {
        const res = await this.$request(PATH.RANTALCOMPANY_GROUP.LIST).catch().enqueue()
        this.groups = Array.isArray(res) ? res : ((res && res.data) || [])
      } catch (e) {
        this.groups = []
      }
    },
    applyPreset(key) {
      this.periodKey = key
      const now = DateTime.local()
      if (this.dateType === "M") {
        const month = key === "lastMonth" ? now.minus({months: 1}) : now
        const text = month.toFormat("yyyy-MM")
        this.startInput = text
        this.endInput = text
      } else {
        let start = now.startOf("day")
        let end = now.startOf("day")
        if (key === "7d") start = now.minus({days: 6}).startOf("day")
        else if (key === "30d") start = now.minus({days: 29}).startOf("day")
        else if (key === "thisMonth") start = now.startOf("month")
        else if (key === "lastMonth") {
          start = now.minus({months: 1}).startOf("month")
          end = now.minus({months: 1}).endOf("month").startOf("day")
        }
        this.startInput = start.toFormat("yyyy-MM-dd")
        this.endInput = end.toFormat("yyyy-MM-dd")
      }
      this.reload()
    },
    onDateTypeChange() {
      const key = this.periodKey === "custom" ? "30d" : this.periodKey
      this.applyPreset(key)
    },
    onManualRange() {
      this.periodKey = "custom"
      this.reload()
    },
    range() {
      if (this.dateType === "M") {
        const start = DateTime.fromFormat(this.startInput, "yyyy-MM")
        const end = DateTime.fromFormat(this.endInput, "yyyy-MM")
        return {
          start: (start.isValid ? start : DateTime.local()).startOf("month"),
          end: (end.isValid ? end : DateTime.local()).endOf("month"),
        }
      }
      const start = DateTime.fromISO(this.startInput)
      const end = DateTime.fromISO(this.endInput)
      return {
        start: (start.isValid ? start : DateTime.local()).startOf("day"),
        end: (end.isValid ? end : DateTime.local()).endOf("day"),
      }
    },
    reload() {
      this.pageNumber = 1
      this.load()
    },
    onPage(page) {
      this.pageNumber = page
      this.load()
    },
    sortBy(key) {
      if (this.sortKey === key) {
        this.descending = !this.descending
      } else {
        this.sortKey = key
        this.descending = key !== "name"
      }
      this.reload()
    },
    async load() {
      const range = this.range()
      this.loading = true
      this.errorMessage = ""
      try {
        const res = await this.$request(PATH.DASHBOARD.SUMMARY)
          .setObject({
            startDate: range.start.toISO({suppressMilliseconds: true}),
            endDate: range.end.toISO({suppressMilliseconds: true}),
            dateType: this.dateType,
            actualYn: this.actualYn,
            rentalcompanyId: this.rentalcompanyId,
            groupId: this.groupId,
            pageNumber: this.pageNumber,
            pageSize: this.pageSize,
            sortKey: this.sortKey,
            descending: this.descending,
          })
          .catch()
          .enqueue()
        this.kpi = res.kpi || {}
        this.collectedTrend = res.collectedTrend || []
        this.addedTrend = res.addedTrend || []
        this.silentTrend = res.silentTrend || []
        this.finalTrend = res.finalTrend || []
        this.contractTrend = res.contractTrend || []
        this.salesTrend = res.salesTrend || []
        this.salesMonths = res.salesMonths || []
        this.billingTrend = res.billingTrend || []
        this.billingContractTrend = res.billingContractTrend || []
        this.billingInkTrend = res.billingInkTrend || []
        this.billingMultiTrend = res.billingMultiTrend || []
        this.directions = res.directions || []
        this.insights = res.insights || []
        this.companies = res.companies || []
        this.totalCount = res.totalCount || 0
      } catch (e) {
        this.errorMessage = "업무 현황을 불러오지 못했습니다."
      } finally {
        this.loading = false
      }
    },
    openCompany(row) {
      this.rentalcompanyId = row.rentalcompanyId
      this.selectedName = row.rentalcompanyName
      this.reload()
    },
    applyRoute() {
      const query = this.$route.query || {}
      this.routeStamp = this.$route.fullPath
      if (query.dateType === "M" || query.dateType === "R") this.dateType = query.dateType
      if (query.start) this.startInput = String(query.start)
      if (query.end) this.endInput = String(query.end)
      if (query.periodKey) this.periodKey = String(query.periodKey)
      this.rentalcompanyId = query.rentalcompanyId ? Number(query.rentalcompanyId) : null
      this.selectedName = query.name ? String(query.name) : ""
    },
    clearCompany() {
      this.rentalcompanyId = null
      this.selectedName = ""
      const target = {name: "managertnbox.dashboard"}
      this.routeStamp = this.$router.resolve(target).route.fullPath
      this.$router.replace(target).catch(() => {})
      this.reload()
    },
    openInsight(item) {
      if (item.rentalcompanyId) {
        this.rentalcompanyId = item.rentalcompanyId
        this.selectedName = item.rentalcompanyName
        this.reload()
        return
      }
      if (item.routeName) {
        this.$router.push({name: item.routeName})
      }
    },
    directionText(key) {
      const found = this.directions.find(item => item.key === key)
      if (!found) return ""
      return "증가 " + found.up + " · 유지 " + found.flat + " · 감소 " + found.down
    },
    maxOf(points) {
      return points.reduce((max, point) => Math.max(max, Number(point.value || 0)), 0)
    },
    barHeight(value, max) {
      const number = Number(value || 0)
      if (!max || number <= 0) return "0%"
      return Math.max(4, Math.round(number * 100 / max)) + "%"
    },
    chartFrame() {
      return {width: 760, top: 36, bottom: 36, height: 240 - 36 - 36, left: 86, right: 56}
    },
    chartScale(series) {
      const rows = series || []
      const values = rows.map((point) => Number(point.value) || 0)
      const max = Math.max.apply(null, values.concat([1]))
      const min = Math.min.apply(null, values.concat([0]))
      const span = max - min
      const floor = span > 0 && min > 0 && span / max < 0.25 ? Math.max(0, min - span * 0.6) : 0
      const ceiling = Math.max(max, floor + 1)
      return {values, floor, ceiling}
    },
    chartY(value, scale) {
      const frame = this.chartFrame()
      const ratio = (value - scale.floor) / (scale.ceiling - scale.floor)
      return frame.top + (1 - ratio) * frame.height
    },
    billingScale() {
      const values = [this.billingTrend, this.billingContractTrend, this.billingInkTrend, this.billingMultiTrend]
        .reduce((all, series) => all.concat((series || []).map((point) => Number(point.value) || 0)), [])
      const max = Math.max.apply(null, values.concat([1]))
      return {floor: 0, ceiling: max}
    },
    moneyAt(series, index) {
      const point = (series || [])[index]
      return this.money(point && point.value)
    },
    finalScale() {
      const values = (this.finalTrend || []).map((point) => Number(point.value) || 0)
      const max = Math.max.apply(null, values.concat([1]))
      const min = values.length ? Math.min.apply(null, values) : 0
      const span = Math.max(max - min, 1)
      return {floor: Math.max(0, min - span * 0.35), ceiling: max + span * 0.35}
    },
    collectScale() {
      const values = [this.addedTrend, this.collectedTrend, this.finalTrend, this.silentTrend]
        .reduce((all, series) => all.concat((series || []).map((point) => Number(point.value) || 0)), [])
      const max = Math.max.apply(null, values.concat([1]))
      return {floor: 0, ceiling: max}
    },
    chartAxis(series, scaleOverride) {
      const scale = scaleOverride || this.chartScale(series)
      const mid = scale.floor + (scale.ceiling - scale.floor) / 2
      return [scale.ceiling, mid, scale.floor].map((value) => ({
        value: value,
        y: this.chartY(value, scale),
        text: this.count(Math.round(value)),
      }))
    },
    chartPoints(series, asMoney, scaleOverride) {
      const rows = series || []
      const ownValues = rows.map((point) => Number(point.value) || 0)
      const scale = scaleOverride || this.chartScale(series)
      const count = rows.length || 1
      const frame = this.chartFrame()
      return rows.map((point, index) => {
        const value = ownValues[index]
        const x = count === 1 ? (frame.left + 760 - frame.right) / 2 : frame.left + (index * (760 - frame.left - frame.right) / (count - 1))
        const y = this.chartY(value, scale)
        return {
          x: x,
          y: y,
          labelY: Math.max(14, y - 8),
          month: String(point.statDate || "").slice(0, 7),
          text: asMoney ? this.money(value) : this.count(value),
          predicted: !!point.predicted,
        }
      })
    },
    poly(points) {
      return points.map((point) => point.x + "," + point.y).join(" ")
    },
    trendCaption(series, asMoney) {
      if (!series || series.length < 2) return ""
      const first = series[0]
      const last = series[series.length - 1]
      const format = asMoney ? this.money : this.count
      return String(first.statDate || "").slice(0, 7) + " " + format(first.value) + " → " + String(last.statDate || "").slice(0, 7) + " " + format(last.value)
    },
    pointTitle(point, asMoney) {
      const value = asMoney ? this.money(point.value) : this.count(point.value)
      return (point.statDate || "") + " " + value
    },
    finalText(point) {
      const text = this.count(point.value)
      return point && point.predicted ? text + " 예측" : text
    },
    count(value) {
      return Number(value || 0).toLocaleString("ko-KR")
    },
    money(value) {
      return Number(value || 0).toLocaleString("ko-KR", {maximumFractionDigits: 0})
    },
    rate(success, total) {
      const base = Number(total || 0)
      if (!base) return "-"
      return (Math.round(Number(success || 0) * 1000 / base) / 10) + "%"
    },
  },
}
</script>

<style scoped>
.header-tools {
  display: flex;
  align-items: center;
  margin-left: 12px;
}
.filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  padding: 4px 0 8px;
  gap: 8px;
}
.period-label, .period-tilde { color: #475467; font-size: 13px; }
.period-presets { display: flex; gap: 4px; }
.period-chip {
  border: 1px solid #d0d5dd;
  background: #fff;
  border-radius: 16px;
  padding: 4px 10px;
  font-size: 13px;
  cursor: pointer;
}
.period-chip.active {
  background: #0f766e;
  border-color: #0f766e;
  color: #fff;
}
.period-select, .period-date {
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  height: 32px;
  padding: 0 8px;
  background: #fff;
}
.line-chart { width: 100%; height: 240px; }
.line { fill: none; stroke-width: 2.5; }
.grid { stroke: #eaecf0; stroke-width: 1; }
.axis-label { font-size: 11px; fill: #667085; }
.line-collect, .dot-collect { stroke: #2f6fed; }
.dot-collect { fill: #2f6fed; }
.line-heard, .dot-heard { stroke: #0f766e; }
.dot-heard { fill: #0f766e; }
.line-silent, .dot-silent { stroke: #b42318; }
.dot-silent { fill: #b42318; }
.line-final { stroke: #6941c6; stroke-width: 3; }
.dot-final { fill: #6941c6; stroke: #6941c6; }
.legend { display: flex; gap: 14px; margin: 6px 0 2px; font-size: 12px; color: #344054; }
.leg::before { content: ""; display: inline-block; width: 10px; height: 10px; border-radius: 50%; margin-right: 4px; }
.leg-add::before { background: #2f6fed; }
.leg-heard::before { background: #0f766e; }
.leg-final::before { background: #6941c6; }
.leg-silent::before { background: #b42318; }
.coord { width: 100%; margin-top: 8px; }
.coord th, .coord td { text-align: right; font-size: 12px; }
.coord th:first-child { text-align: left; }
.pc-main { font-weight: 700; color: #1d2939; }
.line-contract, .dot-contract { stroke: #0f766e; }
.dot-contract { fill: #0f766e; }
.line-sales, .dot-sales { stroke: #b45309; }
.dot-sales { fill: #b45309; }
.line-bill, .dot-bill { stroke: #1d4ed8; }
.dot-bill { fill: #1d4ed8; }
.line-bill { stroke-width: 3; }
.line-bill-contract { stroke: #0f766e; stroke-width: 2; }
.line-bill-ink { stroke: #2563eb; stroke-width: 2; stroke-dasharray: 5 3; }
.line-bill-multi { stroke: #7c3aed; stroke-width: 2; stroke-dasharray: 2 3; }
.leg-bill::before { background: #1d4ed8; }
.leg-bill-contract::before { background: #0f766e; }
.leg-bill-ink::before { background: #2563eb; }
.leg-bill-multi::before { background: #7c3aed; }
.y-label { font-size: 11px; fill: #101828; }
.x-label { font-size: 11px; fill: #475467; }
.dashboard-scroll {
  height: 100%;
  overflow-y: auto;
}
.dashboard {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 24px;
}
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 8px;
}
.kpi-card, .panel {
  background: #fff;
  border: 1px solid #e3e8ef;
  border-radius: 8px;
}
.kpi-card {
  padding: 10px 12px;
}
.kpi-label, .hint, .sub, .kpi-sub {
  color: #667085;
  font-size: 12px;
}
.kpi-value {
  font-size: 18px;
  font-weight: 700;
  color: #1d2939;
}
.panel {
  padding: 12px 14px;
}
.panel header {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: baseline;
}
.panel h3 {
  margin: 0;
  font-size: 15px;
}
.panel header span, .month-line {
  color: #475467;
  font-size: 12px;
}
.trend-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}
.bars {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 88px;
  margin-top: 8px;
}
.bar-col {
  flex: 1;
  height: 100%;
  display: flex;
  align-items: flex-end;
}
.bar {
  width: 100%;
  background: #2f6fed;
  border-radius: 2px 2px 0 0;
  min-height: 0;
}
.bars-contract .bar { background: #0f766e; }
.bars-sales .bar { background: #b45309; }
.month-line {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
}
.insight {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  text-align: left;
  border: 0;
  border-left: 4px solid #b45309;
  background: #fff7ed;
  margin-top: 8px;
  padding: 8px 10px;
  cursor: pointer;
}
.insight.danger {
  border-left-color: #b42318;
  background: #fef3f2;
}
.insight strong { font-size: 13px; }
.insight span { font-size: 13px; color: #344054; }
.table-wrap { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; font-size: 12px; }
th, td { padding: 8px 6px; border-bottom: 1px solid #eaecf0; white-space: nowrap; text-align: right; }
th:first-child, td:first-child { text-align: left; }
th { cursor: pointer; color: #475467; font-weight: 600; }
tbody tr { cursor: pointer; }
tbody tr:hover { background: #f8fafc; }
.name { font-weight: 600; color: #1d2939; }
.empty { color: #667085; padding: 12px 0; }
.pager { display: flex; justify-content: center; margin-top: 8px; }
.loading-box, .error-box { padding: 24px; }
.error-box { color: #b42318; }
@media (max-width: 1200px) {
  .kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
